import { useState, useEffect } from 'react'
import { authClient } from '../../lib/auth-client'
import { queryClient } from '../../api/query-client'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog'
import {
    Monitor,
    Smartphone,
    Globe,
    Clock,
    LogOut,
    Trash2,
    Shield,
    KeyRound,
} from 'lucide-react'
import { Warning } from '@mui/icons-material'
import moment from 'moment'

const getDeviceIcon = (userAgent: string) => {
    if (!userAgent) return Monitor
    const ua = userAgent.toLowerCase()
    if (
        ua.includes('mobile') ||
        ua.includes('android') ||
        ua.includes('iphone')
    )
        return Smartphone
    return Monitor
}

const getBrowserName = (userAgent: string) => {
    if (!userAgent) return 'Unknown browser'
    const ua = userAgent.toLowerCase()
    if (ua.includes('firefox')) return 'Firefox'
    if (ua.includes('edg')) return 'Edge'
    if (ua.includes('chrome')) return 'Chrome'
    if (ua.includes('safari')) return 'Safari'
    if (ua.includes('opera') || ua.includes('opr')) return 'Opera'
    return 'Unknown browser'
}

const getOSName = (userAgent: string) => {
    if (!userAgent) return ''
    const ua = userAgent.toLowerCase()
    if (ua.includes('windows')) return 'Windows'
    if (ua.includes('mac')) return 'macOS'
    if (ua.includes('linux')) return 'Linux'
    if (ua.includes('android')) return 'Android'
    if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS'
    return ''
}

const ProfileRoute = () => {
    const { data: session } = authClient.useSession()
    const user = session?.user as any
    const navigate = useNavigate()

    const [sessions, setSessions] = useState<any[]>([])
    const [sessionsLoading, setSessionsLoading] = useState(true)
    const [revoking, setRevoking] = useState<string | null>(null)
    const [showLogoutAll, setShowLogoutAll] = useState(false)
    const [loggingOutAll, setLoggingOutAll] = useState(false)
    const [showChangePassword, setShowChangePassword] = useState(false)
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })
    const [passwordError, setPasswordError] = useState('')
    const [passwordSuccess, setPasswordSuccess] = useState(false)
    const [changingPassword, setChangingPassword] = useState(false)

    const fetchSessions = async () => {
        setSessionsLoading(true)
        const res = await authClient.listSessions()
        if (res.data) {
            setSessions(res.data)
        }
        setSessionsLoading(false)
    }

    useEffect(() => {
        fetchSessions()
    }, [])

    const handleRevokeSession = async (token: string) => {
        setRevoking(token)
        await authClient.revokeSession({ token })
        setSessions((prev) => prev.filter((s) => s.token !== token))
        setRevoking(null)
    }

    const handleRevokeOtherSessions = async () => {
        setLoggingOutAll(true)
        await authClient.revokeOtherSessions()
        await fetchSessions()
        setLoggingOutAll(false)
        setShowLogoutAll(false)
    }

    const handleSignOut = async () => {
        await authClient.signOut()
        queryClient.clear()
        navigate('/login')
    }

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setPasswordError('')
        setPasswordSuccess(false)

        if (passwordForm.newPassword.length < 8) {
            setPasswordError('New password must be at least 8 characters.')
            return
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError('New passwords do not match.')
            return
        }

        setChangingPassword(true)
        const { error } = await authClient.changePassword({
            currentPassword: passwordForm.currentPassword,
            newPassword: passwordForm.newPassword,
        })
        setChangingPassword(false)

        if (error) {
            setPasswordError(error.message || 'Failed to change password.')
        } else {
            setPasswordSuccess(true)
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            })
            setTimeout(() => setShowChangePassword(false), 1500)
        }
    }

    const handleClosePasswordDialog = (open: boolean) => {
        if (!open) {
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            })
            setPasswordError('')
            setPasswordSuccess(false)
        }
        setShowChangePassword(open)
    }

    if (!user) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-4">
                    <div className="h-7 w-40 bg-muted rounded" />
                    <div className="h-48 bg-muted rounded-lg" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    const currentSessionToken = session?.session?.token

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32 max-w-3xl">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                    Profile
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Manage your account and active sessions
                </p>
            </div>

            {/* Account Information */}
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Account
                    </h3>
                </div>
                <div className="p-6">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <div className="space-y-1">
                            <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Name
                            </dt>
                            <dd className="text-sm text-foreground">
                                {user.firstName} {user.lastName}
                            </dd>
                        </div>
                        <div className="space-y-1">
                            <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Email
                            </dt>
                            <dd className="text-sm text-foreground font-mono">
                                {user.email}
                            </dd>
                        </div>
                        <div className="space-y-1">
                            <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Password
                            </dt>
                            <dd>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setShowChangePassword(true)
                                    }
                                    className="text-xs gap-1.5"
                                >
                                    <KeyRound className="h-3 w-3" />
                                    Change Password
                                </Button>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Active Sessions */}
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Active Sessions
                    </h3>
                    {sessions.length > 1 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowLogoutAll(true)}
                            className="text-xs gap-1.5"
                        >
                            <Trash2 className="h-3 w-3" />
                            Revoke Other Sessions
                        </Button>
                    )}
                </div>
                <div className="divide-y divide-border">
                    {sessionsLoading ? (
                        <div className="p-6 space-y-4">
                            {[1, 2].map((i) => (
                                <div
                                    key={i}
                                    className="animate-pulse flex items-center gap-4"
                                >
                                    <div className="h-9 w-9 bg-muted rounded-md" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-40 bg-muted rounded" />
                                        <div className="h-3 w-24 bg-muted rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="px-6 py-8 text-center">
                            <Shield className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                                No active sessions found.
                            </p>
                        </div>
                    ) : (
                        sessions
                            .sort((a, b) => {
                                // Current session first
                                if (a.token === currentSessionToken) return -1
                                if (b.token === currentSessionToken) return 1
                                return (
                                    new Date(b.updatedAt).getTime() -
                                    new Date(a.updatedAt).getTime()
                                )
                            })
                            .map((s) => {
                                const isCurrent =
                                    s.token === currentSessionToken
                                const DeviceIcon = getDeviceIcon(
                                    s.userAgent || ''
                                )
                                const browser = getBrowserName(
                                    s.userAgent || ''
                                )
                                const os = getOSName(s.userAgent || '')

                                return (
                                    <div
                                        key={s.token}
                                        className="px-6 py-4 flex items-center justify-between gap-4"
                                    >
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                                                <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-foreground">
                                                        {browser}
                                                        {os ? ` on ${os}` : ''}
                                                    </p>
                                                    {isCurrent && (
                                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                                            Current
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                                                    {s.ipAddress && (
                                                        <span className="flex items-center gap-1">
                                                            <Globe className="h-3 w-3" />
                                                            {s.ipAddress}
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {moment(
                                                            s.updatedAt
                                                        ).fromNow()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {!isCurrent && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    handleRevokeSession(s.token)
                                                }
                                                disabled={revoking === s.token}
                                                className="text-xs text-muted-foreground hover:text-destructive flex-shrink-0"
                                            >
                                                <LogOut className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                )
                            })
                    )}
                </div>
            </div>

            {/* Sign Out */}
            <div className="rounded-lg border bg-card">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Sign Out
                    </h3>
                </div>
                <div className="p-6 flex items-center justify-between">
                    <div className="space-y-0.5">
                        <p className="text-sm font-medium text-foreground">
                            Sign out of this device
                        </p>
                        <p className="text-xs text-muted-foreground">
                            You will be redirected to the login page.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSignOut}
                        className="gap-1.5"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign Out
                    </Button>
                </div>
            </div>

            {/* Confirm Revoke All Dialog */}
            <Dialog
                open={showLogoutAll}
                onOpenChange={() => setShowLogoutAll(false)}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning className="text-amber-500" />
                            Revoke Other Sessions
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            This will sign out all other devices. Your current
                            session will remain active.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowLogoutAll(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleRevokeOtherSessions}
                            disabled={loggingOutAll}
                        >
                            {loggingOutAll
                                ? 'Revoking...'
                                : 'Revoke All Others'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Change Password Dialog */}
            <Dialog
                open={showChangePassword}
                onOpenChange={handleClosePasswordDialog}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="h-4 w-4" />
                            Change Password
                        </DialogTitle>
                    </DialogHeader>
                    <form
                        onSubmit={handleChangePassword}
                        className="space-y-4 py-2"
                    >
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Current Password
                            </label>
                            <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(e) =>
                                    setPasswordForm((f) => ({
                                        ...f,
                                        currentPassword: e.target.value,
                                    }))
                                }
                                required
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                New Password
                            </label>
                            <input
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(e) =>
                                    setPasswordForm((f) => ({
                                        ...f,
                                        newPassword: e.target.value,
                                    }))
                                }
                                required
                                minLength={8}
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Confirm New Password
                            </label>
                            <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(e) =>
                                    setPasswordForm((f) => ({
                                        ...f,
                                        confirmPassword: e.target.value,
                                    }))
                                }
                                required
                                minLength={8}
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        {passwordError && (
                            <p className="text-xs text-red-600">
                                {passwordError}
                            </p>
                        )}
                        {passwordSuccess && (
                            <p className="text-xs text-green-600">
                                Password changed successfully.
                            </p>
                        )}
                        <DialogFooter className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    handleClosePasswordDialog(false)
                                }
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={changingPassword}
                            >
                                {changingPassword
                                    ? 'Changing...'
                                    : 'Change Password'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export { ProfileRoute }
